<template>
  <q-layout v-if="user" container view="lHh Lpr lff" class="window-height">
    <q-header :class="{ 'bg-dark': $q.dark.isActive, 'bg-white text-black': !$q.dark.isActive }">
      <teleport-target id="toolbar-portal" />
    </q-header>
    <q-drawer
      :model-value="true"
      behavior="desktop"
      no-mini-animation
      :mini="isMini"
      :width="230"
      bordered
      side="left"
      class="column no-wrap"
    >
      <q-list>
        <q-item class="q-mt-sm q-mb-lg">
          <router-link to="/">
            <q-item-section v-if="!isMini">
              <img
                v-if="$q.dark.isActive"
                id="hd-logo"
                alt="HelloData Logo"
                src="/hd-logo-white.svg"
                width="200px"
                height="53px"
              />
              <img
                v-else
                id="hd-logo"
                src="/hd-logo.svg"
                width="200px"
                height="53px"
                alt="HelloData Logo"
              />
            </q-item-section>
            <q-item-section v-else avatar>
              <q-img src="/hd-wave-logo.svg" no-spinner width="20px" height="20px" />
            </q-item-section>
          </router-link>
        </q-item>
        <div class="intercom-drawer-actions">
          <template v-if="hasPropertyReports">
            <q-item v-if="!isMini" dense>
              <address-autocomplete
                dense
                placeholder="Quick Report..."
                surface-reports
                @new-address="openOverview($event.id)"
              />
            </q-item>
            <q-item v-else dense clickable>
              <q-item-section avatar>
                <q-avatar icon="search" />
              </q-item-section>
              <q-menu transition-duration="100">
                <q-banner>
                  <address-autocomplete
                    dense
                    placeholder="Quick Report..."
                    surface-reports
                    @new-address="openOverview($event.id)"
                  />
                </q-banner>
              </q-menu>
            </q-item>
            <q-item
              dense
              clickable
              active-class="text-primary indented-primary"
              :to="{ name: 'New Development' }"
            >
              <q-item-section avatar>
                <q-avatar icon="add_circle" size="35px" />
              </q-item-section>
              <q-item-section> New Development </q-item-section>
            </q-item>
          </template>
          <q-item-label header class="menu-header q-mt-md">
            <q-item-section> NAVIGATION </q-item-section>
          </q-item-label>
          <q-item
            id="intercom-dashboard"
            dense
            clickable
            active-class="text-primary indented-primary"
            :to="{ name: 'Dashboard' }"
          >
            <q-item-section avatar>
              <q-avatar icon="space_dashboard" size="35px" />
            </q-item-section>
            <q-item-section>Dashboard</q-item-section>
          </q-item>
          <q-item
            v-if="hasPropertyReports"
            id="intercom-regional-reports"
            dense
            clickable
            active-class="text-primary indented-primary"
            :to="{ name: 'Regional Reports' }"
          >
            <q-item-section avatar>
              <q-avatar icon="map" size="35px" />
            </q-item-section>
            <q-item-section>Regional Reports</q-item-section>
          </q-item>
          <q-item
            v-if="hasQueryBuilderFeature"
            id="intercom-dataset-builder"
            dense
            clickable
            active-class="text-primary indented-primary"
            :to="{ name: 'Datasets' }"
          >
            <q-item-section avatar>
              <q-avatar icon="data_usage" size="35px" />
            </q-item-section>
            <q-item-section> Query Builder </q-item-section>
          </q-item>
          <q-item
            v-if="hasMarketFinderFeature"
            id="intercom-market-finder"
            dense
            clickable
            active-class="text-primary indented-primary"
            :to="{ name: 'Similar Markets' }"
          >
            <q-item-section avatar>
              <q-avatar icon="location_city" size="35px" />
            </q-item-section>
            <q-item-section> Market Finder </q-item-section>
          </q-item>
        </div>
        <template v-if="hasPropertyReports && !isMini">
          <q-item-label header class="menu-header q-mt-md">
            <q-item-section> RECENT REPORTS </q-item-section>
          </q-item-label>
          <q-item v-if="recentReports.length === 0" dense>
            <div class="text-grey-6">No recent reports yet</div>
          </q-item>
          <q-item
            v-for="report in recentReports"
            :key="report.id"
            dense
            clickable
            :to="{ name: 'Property Report', params: { reportId: report.id } }"
            active-class="text-accent indented-accent"
          >
            <q-item-section>
              <q-item-label>
                <div class="ellipsis">{{ report.name }}</div>
              </q-item-label>
            </q-item-section>
          </q-item>
        </template>
      </q-list>
      <q-space />
      <q-item dense>
        <q-item-section>
          <q-chip v-if="propertyReportsPastDue" :ripple="false" class="text-deep-orange" outline>
            <div class="text-center full-width">Past Due</div>
            <q-tooltip>Your subscription is past due.</q-tooltip>
          </q-chip>
        </q-item-section>
      </q-item>
      <q-item clickable dense @click="toggleDrawer">
        <q-item-section avatar>
          <q-avatar v-if="!isMini" icon="arrow_back" size="35px" />
          <q-avatar v-else icon="arrow_forward" />
        </q-item-section>
        <q-item-section>Close Sidebar</q-item-section>
      </q-item>
      <q-item id="intercom-organization" dense clickable :to="{ name: 'Organization' }">
        <q-item-section avatar>
          <q-avatar icon="business" size="35px" />
        </q-item-section>
        <q-item-section>Organization</q-item-section>
      </q-item>
      <q-item id="intercom-support-feedback" clickable dense @click="openIntercom">
        <q-item-section avatar>
          <q-avatar icon="support_agent" size="35px" />
        </q-item-section>
        <q-item-section>
          <div>
            Support & Feedback
            <q-badge v-if="intercomCount > 0" :label="intercomCount" color="red" class="q-ml-xs" />
          </div>
        </q-item-section>
      </q-item>
      <q-list id="intercom-account">
        <q-separator />
        <q-item clickable>
          <q-item-section top avatar>
            <q-avatar size="28px" round>
              <q-img
                width="28px"
                :src="user.picture"
                alt="User Picture"
                referrerpolicy="no-referrer"
                no-spinner
              />
            </q-avatar>
          </q-item-section>
          <q-item-section class="ellipsis">
            <q-item-label v-if="userName">{{ userName }}</q-item-label>
            <q-item-label v-else>{{ user.email }}</q-item-label>
            <q-item-label caption>
              {{ accountSubtitle }}
            </q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-icon name="more_vert" />
          </q-item-section>
          <q-menu transition-duration="100">
            <q-list>
              <q-item id="intercom-profile" clickable :to="{ name: 'Profile' }">
                <q-item-section avatar>
                  <q-icon name="person" />
                </q-item-section>
                <q-item-section>Profile</q-item-section>
              </q-item>
              <q-item
                v-if="isHelloDataEmployee"
                id="intercom-admin"
                clickable
                :to="{ name: 'Admin' }"
              >
                <q-item-section avatar>
                  <q-icon name="admin_panel_settings" />
                </q-item-section>
                <q-item-section>Admin</q-item-section>
              </q-item>
              <q-item
                v-if="isAdmin"
                id="intercom-invite-people"
                clickable
                :to="{ name: 'Organization' }"
              >
                <q-item-section avatar>
                  <q-icon name="mail_outline" />
                </q-item-section>
                <q-item-section> Invite Users </q-item-section>
              </q-item>
              <q-item
                id="intercom-billing"
                clickable
                :disable="!hasBillingPortalUrl"
                :to="{ name: 'Billing' }"
              >
                <q-item-section avatar>
                  <q-icon name="account_balance" />
                </q-item-section>
                <q-item-section> Billing </q-item-section>
              </q-item>
              <q-separator />
              <q-item
                v-close-popup
                clickable
                href="https://www.hellodata.ai/learning-hub"
                target="_blank"
              >
                <q-item-section avatar>
                  <q-icon name="help" />
                </q-item-section>
                <q-item-section>
                  <div class="flex">
                    <div>Help Center</div>
                    <q-icon name="launch" class="q-ml-sm q-my-auto" />
                  </div>
                </q-item-section>
              </q-item>
              <q-item clickable @click="toggleDarkMode">
                <q-item-section avatar>
                  <q-icon :name="Dark.isActive ? 'light_mode' : 'dark_mode'" />
                </q-item-section>
                <q-item-section>{{ Dark.isActive ? 'Light' : 'Dark' }} Mode</q-item-section>
              </q-item>
              <q-item v-close-popup clickable>
                <q-item-section avatar>
                  <q-icon name="logout" />
                </q-item-section>
                <q-item-section class="text-red" @click="signOut"> Sign Out </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-item>
      </q-list>
    </q-drawer>

    <teleport-target id="right-drawer-portal" />

    <q-page-container class="full-height bg-grey-2" :class="{ 'bg-grey-10': $q.dark.isActive }">
      <q-page padding class="q-pb-xl">
        <router-view></router-view>
      </q-page>
    </q-page-container>
  </q-layout>
  <div v-else class="window-height">
    <q-inner-loading :showing="!user" color="secondary" />
  </div>
</template>

<script lang="ts">
import { RouterView } from 'vue-router'
import { Dialog, Dark } from 'quasar'
import LogRocket from 'logrocket'

import { useUserStore } from '@/stores/user'
import { useStripeStore } from '@/stores/stripe'
import AddressAutocomplete from '@/components/PropertyReports/AddressAutocomplete.vue'
import slackApi from '@/api/slack'
import { FeatureFlag } from 'common'
import type { OrganizationAttributes } from 'common'

declare const Intercom: (action: string, ...args: any[]) => void // eslint-disable-line

export default {
  name: 'AppView',
  components: { RouterView, AddressAutocomplete },
  data() {
    return {
      Dark,
      mounted: false,
      isMini: false,
      intercomCount: 0,
      handleMonitoringKeyboardShortcut: null as ((e: KeyboardEvent) => void) | null
    }
  },
  computed: {
    hasMarketFinderFeature() {
      return useUserStore().hasFeatureFlag(null, FeatureFlag.MARKET_FINDER)
    },
    hasQueryBuilderFeature() {
      return useUserStore().hasQueryBuilder
    },
    isHelloDataEmployee() {
      return useUserStore().isHelloDataEmployee
    },
    mobileWidth() {
      return this.$q.screen.lt.md
    },
    recentReports() {
      return useUserStore().getRecentReports()
    },
    accountSubtitle(): string {
      return useUserStore().organization?.display_name || 'Account Settings'
    },
    userName(): string | undefined {
      const user = this.user
      if (user && user?.given_name && user?.family_name) {
        return `${user.given_name} ${user.family_name}`
      }
      return user?.email
    },
    user() {
      return this.$auth0.user.value
    },
    hasPropertyReports(): boolean {
      return useStripeStore().hasPropertyReports
    },
    hasMarketReports(): boolean {
      return useStripeStore().hasMarketReports
    },
    hasBillingPortalUrl() {
      return useStripeStore().hasCustomerPortal
    },
    organizationInvitation() {
      return useUserStore().organizationInvitation
    },
    propertyReportsPastDue() {
      if (!useUserStore().isAdmin) {
        // Only admin/owners should see this
        return false
      }
      const sub = useStripeStore().subscriptions.find((_) =>
        _.products.includes(import.meta.env.VITE_STRIPE_PROPERTY_REPORTS_PRODUCT)
      )
      return sub?.past_due || null
    },
    organization() {
      return useUserStore().organization
    },
    isAdmin() {
      return useUserStore().isAdmin
    }
  },
  watch: {
    mobileWidth() {
      this.updateDrawer()
    },
    organizationInvitation() {
      if (this.organizationInvitation) {
        Dialog.create({
          title: 'Organization Invitation',
          message: `You have been invited to join <b>${this.organizationInvitation.display_name}</b>. Would you like to accept?`,
          persistent: true,
          html: true, // technically, we should escape the message
          ok: {
            label: 'Accept',
            color: 'primary',
            unelevated: true,
            noCaps: true
          },
          cancel: {
            label: 'Deny',
            color: 'negative',
            unelevated: true,
            noCaps: true
          }
        })
          .onOk(() => {
            this.acceptInvitation()
          })
          .onCancel(() => {
            this.denyInvitation()
          })
      }
    }
  },
  created() {
    const {
      initOrganizationMembers,
      initOrganization,
      initInvitation,
      initPeopleInOrgOrShared,
      initWebAuth,
      initHelpSoftware
    } = useUserStore()

    const { initCustomerSubscriptions, getCustomerPortal, initPropertyReportTrialConsumed } =
      useStripeStore()

    initInvitation()
    initPeopleInOrgOrShared()
    initWebAuth()
    initPropertyReportTrialConsumed()

    Promise.all([initOrganization(), initCustomerSubscriptions(), initOrganizationMembers()]).then(
      ([org]: [OrganizationAttributes | null, void, void]) => {
        // for now, we need to get both the org data and what subscriptions the user has
        if (this.user && this.user.email) {
          this.$tracker.signedIn(this.user, org?.display_name, org?.id)
        }
        getCustomerPortal()
        initHelpSoftware()
        Intercom('onUnreadCountChange', (unreadCount: number) => {
          this.intercomCount = unreadCount
        })
      }
    )

    this.updateDrawer()
  },
  mounted() {
    // monitor clicks on the SHIFT + UP key press, for internalOnly users
    if (this.isHelloDataEmployee) {
      this.handleMonitoringKeyboardShortcut = (e) => {
        if (e.key === 'ArrowUp' && e.shiftKey) {
          const date = new Date()
          const msg = `User ${this.user?.email} pressed SHIFT + UP key at ${date.toISOString()} on ${window.location.href}`
          LogRocket.error(msg)
          const sessionURL = LogRocket.sessionURL
          if (sessionURL) {
            slackApi.sendError(msg + ' Logrocket Session URL: ' + sessionURL)
          } else {
            slackApi.sendError(msg + ' but no session URL')
          }
        }
      }
      window.addEventListener('keydown', this.handleMonitoringKeyboardShortcut)
    }
  },
  unmounted() {
    if (this.handleMonitoringKeyboardShortcut) {
      window.removeEventListener('keydown', this.handleMonitoringKeyboardShortcut)
    }
  },
  methods: {
    toggleDrawer() {
      const currentlyMini = this.isMini
      this.$tracker.toggledSidebar(!currentlyMini)
      this.isMini = !currentlyMini
    },
    updateDrawer() {
      this.isMini = this.mobileWidth
    },
    openOverview(id: string) {
      this.$router.push({ name: 'New Property Report', query: { propertyId: id } })
    },
    openIntercom() {
      this.$tracker.openedIntercom()
      Intercom('show')
    },
    signOut() {
      this.$tracker.signedOut()
      this.$auth0.logout({ logoutParams: { returnTo: 'https://www.hellodata.ai' } })
    },
    denyInvitation() {
      useUserStore()
        .denyInvitation()
        .then(() => {
          this.$tracker.receivedInvitation(false)
        })
    },
    acceptInvitation() {
      useUserStore()
        .acceptInvitation()
        .then(() => {
          this.$tracker.receivedInvitation(true)
          this.$auth0.getAccessTokenSilently({ cacheMode: 'off' }).then(() => {
            useUserStore().initOrganizationMembers()
            useUserStore().initOrganization()
            useUserStore().initPeopleInOrgOrShared()
            useUserStore().initWebAuth()
          })
        })
    },
    toggleDarkMode() {
      localStorage.setItem('prefer-dark', (!Dark.isActive).toString())
      Dark.toggle()
    }
  }
}
</script>

<style lang="scss" scoped>
.q-avatar {
  width: inherit;
}
.q-item__section--avatar {
  min-width: inherit;
}
.menu-header {
  font-size: 13px;
  padding-bottom: 8px;
}
.indented-accent {
  box-shadow: 4 0 0 -2px $accent inset;
}
.indented-primary {
  box-shadow: 4 0 0 -2px $primary inset;
}
</style>
